import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';

import type { TicketDetail } from '@nightshift/ui-types';

import { Markdown } from '../../components/markdown.tsx';
import { Spinner, StateChip } from '../../components/primitives.tsx';
import { api, ApiError } from '../../live/api.ts';
import './dependency-preview.css';

export function DependencyPreview({
  previewRef,
  ticketKey,
  projectSlug,
  poke,
  onClose,
}: {
  previewRef: RefObject<HTMLElement | null>;
  ticketKey: string;
  projectSlug: string | null;
  poke: string;
  onClose: () => void;
}) {
  const [detail, setDetail] = useState<TicketDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [updating, setUpdating] = useState(false);
  const openerRef = useRef<HTMLElement | null>(null);
  const requestRef = useRef(0);
  // Whether focus was inside the preview, tracked on the way in and out. A refresh that
  // re-renders the element holding focus drops focus to the body without any blur event, so
  // this is the only record that the preview had it.
  const focusInsideRef = useRef(false);

  // Refreshes in place: the content that is on screen stays there until the next detail has
  // arrived. Clearing it first showed a spinner, reset the scroll position and unmounted the
  // focused element on every board update, including one for a comment on another ticket.
  const load = useCallback(async () => {
    const request = ++requestRef.current;
    setUpdating(true);
    try {
      const next = await api.ticket(ticketKey, projectSlug ?? undefined);
      if (request !== requestRef.current) return;
      setDetail(next);
      setError(null);
    } catch (caught) {
      if (request !== requestRef.current) return;
      setError(
        caught instanceof ApiError && caught.status === 401
          ? 'Your session expired. Sign in again to read this ticket.'
          : caught instanceof Error
            ? caught.message
            : String(caught),
      );
    } finally {
      if (request === requestRef.current) setUpdating(false);
    }
  }, [projectSlug, ticketKey]);

  useEffect(() => {
    setDetail(null);
    setError(null);
    void load();
    return () => {
      requestRef.current += 1;
    };
  }, [load]);

  useEffect(() => {
    if (detail) void load();
    // A live update refreshes loaded content without turning the initial loading state into
    // two requests. The key only moves when the previewed ticket itself changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poke]);

  useEffect(() => {
    // If the refresh removed the element that had focus, focus falls to the body, where the
    // drawer's keyboard handling no longer applies. Put it on the preview itself instead.
    if (focusInsideRef.current && document.activeElement === document.body) {
      previewRef.current?.focus();
    }
  }, [detail, previewRef]);

  useEffect(() => {
    openerRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    previewRef.current?.querySelector<HTMLElement>('button')?.focus();
    return () => {
      if (openerRef.current?.isConnected) openerRef.current.focus();
    };
  }, [previewRef]);

  return (
    <>
      <div
        className="dependency-preview-dismiss"
        role="presentation"
        onClick={onClose}
        data-testid="dependency-preview-dismiss"
      />
      <aside
        ref={previewRef}
        className="dependency-preview"
        role="dialog"
        aria-labelledby="dependency-preview-title"
        tabIndex={-1}
        onFocusCapture={() => {
          focusInsideRef.current = true;
        }}
        onBlurCapture={(event) => {
          // Focus moving to another element, anywhere; a removed element fires nothing.
          if (event.relatedTarget instanceof Node && !event.currentTarget.contains(event.relatedTarget)) {
            focusInsideRef.current = false;
          }
        }}
      >
        <header className="dependency-preview-head">
          <div className="dependency-preview-identity">
            <span className="dependency-preview-context">
              Related ticket preview
              {updating && detail ? <span className="dependency-preview-updating"> · updating</span> : null}
            </span>
            <div className="dependency-preview-keyline">
              <span className="mono">{ticketKey}</span>
              {detail ? <StateChip state={detail.ticket.state} size="md" /> : null}
            </div>
            <h2 id="dependency-preview-title">
              {detail?.ticket.title ?? (error ? 'Ticket unavailable' : 'Loading ticket…')}
            </h2>
          </div>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={onClose}
            aria-label="Close dependency preview"
            title="Close dependency preview (Esc)"
          >
            ✕
          </button>
        </header>

        <div className="dependency-preview-body scroll-y">
          {error && detail ? (
            <p className="dependency-preview-stale" role="alert">
              Could not refresh {ticketKey}: {error}
            </p>
          ) : null}
          {error && !detail ? (
            <div className="dependency-preview-error" role="alert">
              <h3>Could not load {ticketKey}</h3>
              <p>{error}</p>
              <button type="button" className="btn btn-sm" onClick={() => void load()}>
                Try again
              </button>
            </div>
          ) : !detail ? (
            <div className="dependency-preview-loading">
              <Spinner size={18} />
              <span>Loading {ticketKey}…</span>
            </div>
          ) : (
            <div className="dependency-preview-content">
              <section aria-labelledby="dependency-preview-problem">
                <h3 id="dependency-preview-problem">Problem</h3>
                <Markdown source={detail.ticket.problem || 'No problem statement supplied.'} />
              </section>
              <section aria-labelledby="dependency-preview-acceptance">
                <h3 id="dependency-preview-acceptance">Acceptance criteria</h3>
                <Markdown source={detail.ticket.acceptanceCriteria || 'No acceptance criteria supplied.'} />
              </section>
            </div>
          )}
        </div>
      </aside>
    </>
  );
}
